import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MailLockIcon from "@mui/icons-material/MailLock";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  CreateAssignmentModalController,
  CreateAssignmentModalControllerDelegate,
  IUser,
  UserEntry,
} from "../CreateAssignmentModal/controller";
import { IAssignment } from "../controller";
import { environment } from "../../utils/environment";

export interface AllocateRespondentsModalDelegate {
  shouldCloseModal(): void;
  applicationShouldLogout(): void;
  didAllocateRespondents(): void;
}

interface Props {
  assignment: IAssignment;
  delegate: AllocateRespondentsModalDelegate;
}

interface State {
  submissionPhase: "idle" | "inviting" | "done";
  inviteLog: {
    email: string;
    name: string;
    done: boolean;
    error?: string;
    /** Resultado do envio nesta linha; `null` = nenhum e-mail era devido. */
    emailDelivered?: boolean | null;
  }[];
  usersInput: string;
  usersSearchResults: IUser[];
  usersLoading: boolean;
  usersToAssign: UserEntry[];
  activeUserTab: number;
  newUserFirstName: string;
  newUserLastName: string;
  newUserEmail: string;
  newUserRecaptchaToken: string;
}

class AllocateRespondentsModal
  extends React.Component<Props, State>
  implements CreateAssignmentModalControllerDelegate {

  private controller: CreateAssignmentModalController;
  private usersFetchDebounce?: NodeJS.Timeout;
  private closeTimer?: NodeJS.Timeout;
  private recaptchaRef = React.createRef<ReCAPTCHA>();

  constructor(props: Props) {
    super(props);
    this.state = {
      submissionPhase: "idle",
      inviteLog: [],
      usersInput: "",
      usersSearchResults: [],
      usersLoading: false,
      usersToAssign: [],
      activeUserTab: 0,
      newUserFirstName: "",
      newUserLastName: "",
      newUserEmail: "",
      newUserRecaptchaToken: "",
    };
    this.controller = new CreateAssignmentModalController(this);
  }

  // CreateAssignmentModalControllerDelegate — no-ops for unused methods
  didGetInstruments() {}
  didCreateAssignment() {}

  didGetUsers(users: IUser[]) {
    this.setState({ usersSearchResults: users, usersLoading: false });
  }

  applicationShouldLogout() {
    this.props.delegate.applicationShouldLogout();
  }

  /**
   * Inalcançável aqui: o controller só chama isto em `searchInstruments` e
   * `createAssignment`, e este modal usa apenas `searchUsers` (que trata o
   * próprio erro) e `assignSingleUser` (que lança para o log de alocação).
   */
  genericError() {}

  componentWillUnmount() {
    clearTimeout(this.usersFetchDebounce!);
    clearTimeout(this.closeTimer!);
  }

  private searchUsers(filter: string) {
    this.setState({ usersLoading: true });
    clearTimeout(this.usersFetchDebounce!);
    this.usersFetchDebounce = setTimeout(() => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.searchUsers(token, filter.trim() || undefined);
    }, 300);
  }

  private addExistingUserToQueue(user: IUser) {
    const { usersToAssign } = this.state;
    const maxUsers = this.props.assignment.totalApplicationCount;
    if (usersToAssign.length >= maxUsers) return;
    if (usersToAssign.some((u) => u.id === user.uid || u.email === user.email)) return;
    this.setState((prev) => ({
      usersToAssign: [
        ...prev.usersToAssign,
        {
          id: user.uid,
          type: "existing",
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          isInvitation: user.isInvitation,
        },
      ],
      usersInput: "",
      usersSearchResults: [],
    }));
  }

  private addNewUserToQueue() {
    const {
      newUserFirstName,
      newUserLastName,
      newUserEmail,
      newUserRecaptchaToken,
      usersToAssign,
    } = this.state;
    const maxUsers = this.props.assignment.totalApplicationCount;
    if (!newUserFirstName || !newUserLastName || !newUserEmail || !newUserRecaptchaToken) return;
    if (usersToAssign.length >= maxUsers) return;
    if (usersToAssign.some((u) => u.email === newUserEmail)) return;
    this.setState(
      (prev) => ({
        usersToAssign: [
          ...prev.usersToAssign,
          {
            id: `new-${Date.now()}`,
            type: "new",
            firstName: newUserFirstName,
            lastName: newUserLastName,
            email: newUserEmail,
            recaptchaToken: newUserRecaptchaToken,
          },
        ],
        newUserFirstName: "",
        newUserLastName: "",
        newUserEmail: "",
        newUserRecaptchaToken: "",
      }),
      () => {
        this.recaptchaRef.current?.reset();
      }
    );
  }

  private removeFromQueue(id: string) {
    this.setState((prev) => ({
      usersToAssign: prev.usersToAssign.filter((u) => u.id !== id),
    }));
  }

  private async handleAllocate() {
    const { usersToAssign } = this.state;
    const { assignment } = this.props;
    if (usersToAssign.length === 0) return;

    const token = localStorage.getItem("AccessToken")!;
    this.setState({
      submissionPhase: "inviting",
      inviteLog: usersToAssign.map((u) => ({
        email: u.email,
        name: `${u.firstName} ${u.lastName}`,
        done: false,
      })),
    });

    let hadErrors = false;
    // Contado à parte de hadErrors: a associação deu certo, então não é erro —
    // mas é a única coisa que a psicóloga precisa resolver, e o fechamento
    // automático abaixo tiraria o aviso da tela antes de ser lido.
    let undeliveredEmails = false;
    for (let i = 0; i < usersToAssign.length; i++) {
      try {
        const emailDelivered = await this.controller.assignSingleUser(
          token,
          usersToAssign[i],
          assignment.applicationId
        );
        if (emailDelivered === false) {
          undeliveredEmails = true;
        }
        this.setState((prev) => {
          const log = [...prev.inviteLog];
          log[i] = { ...log[i], done: true, emailDelivered };
          return { inviteLog: log };
        });
      } catch (err: any) {
        hadErrors = true;
        const error = err?.response?.data?.message || "Erro ao associar respondente";
        this.setState((prev) => {
          const log = [...prev.inviteLog];
          log[i] = { ...log[i], done: true, error };
          return { inviteLog: log };
        });
      }
    }

    this.setState({ submissionPhase: "done" });
    if (!hadErrors && !undeliveredEmails) {
      this.closeTimer = setTimeout(() => {
        this.props.delegate.didAllocateRespondents();
      }, 3000);
    }
  }

  /**
   * QUAL e-mail este respondente da fila recebe — não SE recebe, porque desde a
   * consolidação do convite todo estado aqui é notificado.
   *
   * `code` leva os códigos de acesso e permite responder sem finalizar o
   * cadastro; `notice` avisa do novo instrumento a quem já entra com e-mail e
   * senha.
   *
   * Uma entrada `new` não é nenhum dos dois: o e-mail é digitado neste modal, e
   * só a rota de convite descobre se ele já pertence a uma conta — nesse caso
   * sai o aviso simples, sem código algum.
   */
  private emailKind(user: UserEntry): "code" | "notice" | "unknown" {
    if (user.type === "new") return "unknown";
    return user.isInvitation === true ? "code" : "notice";
  }

  /**
   * Indicador por linha da fila, logo antes do botão de remover.
   *
   * O ícone precisa carregar o significado sozinho — forma e cor — porque o
   * tooltip só existe no hover e simplesmente não aparece no toque;
   * `titleAccess` entrega a mesma frase ao leitor de tela.
   */
  renderEmailStatusIcon(user: UserEntry) {
    const kind = this.emailKind(user);
    const label =
      kind === "code"
        ? "Receberá um e-mail com o código de acesso e poderá responder sem finalizar o cadastro."
        : kind === "notice"
          ? "Já possui conta: receberá um e-mail avisando do instrumento e entra com e-mail e senha."
          : "Se o e-mail ainda não tiver conta, recebe o código de acesso; se já tiver, recebe o aviso do convite.";

    const icon =
      kind === "code" ? (
        <MailLockIcon
          titleAccess={label}
          fontSize="small"
          color="info"
          sx={{ flexShrink: 0 }}
        />
      ) : kind === "notice" ? (
        <MailOutlineIcon
          titleAccess={label}
          fontSize="small"
          color="action"
          sx={{ flexShrink: 0 }}
        />
      ) : (
        <ForwardToInboxIcon
          titleAccess={label}
          fontSize="small"
          color="info"
          sx={{ flexShrink: 0 }}
        />
      );

    return (
      <Tooltip title={label} arrow placement="left">
        {icon}
      </Tooltip>
    );
  }

  renderUserPanel() {
    const {
      usersSearchResults,
      usersInput,
      usersLoading,
      usersToAssign,
      activeUserTab,
      newUserFirstName,
      newUserLastName,
      newUserEmail,
    } = this.state;
    const maxUsers = this.props.assignment.totalApplicationCount;
    const isFull = usersToAssign.length >= maxUsers;

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color={isFull ? "error.main" : "text.secondary"}>
            {usersToAssign.length} de {maxUsers} licença{maxUsers !== 1 ? "s" : ""} disponíve{maxUsers !== 1 ? "is" : "l"}
          </Typography>
          {isFull && (
            <Typography variant="caption" color="error.main" fontWeight="bold">
              Limite atingido
            </Typography>
          )}
        </Box>

        <Tabs
          value={activeUserTab}
          onChange={(_, val) => this.setState({ activeUserTab: val })}
          variant="fullWidth"
          sx={{
            bgcolor: "grey.200",
            borderRadius: 1,
            "& .MuiTab-root": { borderRadius: 1 },
          }}
        >
          <Tab label="Meus respondentes" sx={{ textTransform: "none" }} />
          <Tab label="Novo respondente" sx={{ textTransform: "none" }} />
        </Tabs>

        {activeUserTab === 0 && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Todo respondente adicionado recebe um e-mail. Quem ainda não
            finalizou o cadastro recebe o código de acesso; quem já possui conta
            recebe um aviso do instrumento e entra com e-mail e senha.
          </Alert>
        )}

        {activeUserTab === 0 && (
          <Autocomplete
            disabled={isFull}
            options={usersSearchResults}
            getOptionLabel={(user: IUser) =>
              `${user.firstName} ${user.lastName} (${user.email})`
            }
            value={null}
            inputValue={usersInput}
            onInputChange={(_, value, reason) => {
              if (reason === "input") {
                this.setState({ usersInput: value }, () => this.searchUsers(value));
              } else if (reason === "clear") {
                this.setState({ usersInput: "" });
              }
            }}
            onChange={(_, user: IUser | null) => {
              if (user) this.addExistingUserToQueue(user);
            }}
            loading={usersLoading}
            filterOptions={(x) => x}
            isOptionEqualToValue={(a, b) => a.uid === b.uid}
            noOptionsText={
              usersInput.length === 0
                ? "Digite para buscar respondentes"
                : "Nenhum respondente encontrado"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar respondente"
                helperText={
                  isFull
                    ? "Limite de licenças atingido"
                    : "Selecione para adicionar à lista abaixo"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
          />
        )}

        {activeUserTab === 1 && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <strong>Se o e-mail ainda não tiver conta</strong>, o respondente
              receberá um e-mail com o código de acesso e poderá responder sem
              finalizar o cadastro. <strong>Se já tiver conta</strong>, receberá
              apenas o aviso do convite e entra com e-mail e senha.
            </Alert>
            <TextField
              label="Nome"
              required
              disabled={isFull}
              value={newUserFirstName}
              onChange={(e) => this.setState({ newUserFirstName: e.target.value })}
            />
            <TextField
              label="Sobrenome"
              required
              disabled={isFull}
              value={newUserLastName}
              onChange={(e) => this.setState({ newUserLastName: e.target.value })}
            />
            <TextField
              label="E-mail"
              required
              disabled={isFull}
              value={newUserEmail}
              onChange={(e) =>
                this.setState({ newUserEmail: e.target.value.toLowerCase().trim() })
              }
            />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ReCAPTCHA
                ref={this.recaptchaRef}
                sitekey={environment.google.recaptchaToken}
                onChange={(token) => this.setState({ newUserRecaptchaToken: token ?? "" })}
                onExpired={() => this.setState({ newUserRecaptchaToken: "" })}
              />
            </Box>
            <Button
              variant="contained"
              disabled={
                isFull ||
                !newUserFirstName ||
                !newUserLastName ||
                !newUserEmail ||
                !this.state.newUserRecaptchaToken
              }
              onClick={() => this.addNewUserToQueue()}
              sx={{ alignSelf: "flex-end" }}
            >
              Adicionar à lista
            </Button>
          </Box>
        )}

        <Divider />

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Serão atribuídos ao alocar:
          </Typography>
          {usersToAssign.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
              Nenhum respondente adicionado ainda.
            </Typography>
          ) : (
            <Stack direction="column" gap={1}>
              {usersToAssign.map((user) => (
                <Chip
                  key={user.id}
                  label={
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                      <Box component="span" flex={1}>
                        {user.type === "new"
                          ? `${user.firstName} ${user.lastName} — convite (${user.email})`
                          : `${user.firstName} ${user.lastName} (${user.email})`}
                      </Box>
                      {this.renderEmailStatusIcon(user)}
                    </Box>
                  }
                  onDelete={() => this.removeFromQueue(user.id)}
                  variant="outlined"
                  color={user.type === "new" ? "primary" : "default"}
                  sx={{
                    height: "auto",
                    width: "100%",
                    justifyContent: "space-between",
                    "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    );
  }

  renderInviteLog() {
    const { inviteLog, submissionPhase } = this.state;
    const allDone = inviteLog.every((e) => e.done);
    const hasErrors = inviteLog.some((e) => e.error);
    // Associado, mas ninguém foi avisado. Não é erro — e é exatamente por isso
    // que o título não pode ser o "Respondentes associados!" sem ressalva.
    const hasUndelivered = inviteLog.some((e) => e.emailDelivered === false);

    const title =
      allDone && submissionPhase === "done"
        ? hasUndelivered
          ? "Respondentes associados, com e-mails não enviados"
          : "Respondentes associados!"
        : "Associando respondentes...";

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
          {title}
        </Typography>
        <Stack direction="column" gap={1.5}>
          {inviteLog.map((entry, i) => (
            <Box key={i} display="flex" flexDirection="column" gap={0.25}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {/* Quatro desfechos, quatro ícones: a associação pode falhar
                    (erro), ou dar certo com o e-mail entregue, não entregue ou
                    nunca devido. Juntar os dois do meio num único tique verde é
                    o que escondia um respondente associado sem aviso nenhum. */}
                {!entry.done ? (
                  <CircularProgress size={18} sx={{ flexShrink: 0 }} />
                ) : entry.error ? (
                  <CancelIcon sx={{ color: "error.main", fontSize: 20, flexShrink: 0 }} />
                ) : entry.emailDelivered === false ? (
                  <MarkEmailUnreadIcon
                    sx={{ color: "warning.main", fontSize: 20, flexShrink: 0 }}
                  />
                ) : entry.emailDelivered === null ? (
                  <InfoOutlinedIcon
                    sx={{ color: "info.main", fontSize: 20, flexShrink: 0 }}
                  />
                ) : (
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 20, flexShrink: 0 }} />
                )}
                <Typography variant="body2">
                  {entry.name}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({entry.email})
                  </Typography>
                </Typography>
              </Box>
              {entry.error && (
                <Typography variant="caption" color="error.main" sx={{ pl: "33.5px" }}>
                  {entry.error}
                </Typography>
              )}
              {!entry.error && entry.emailDelivered === false && (
                <Typography variant="caption" color="warning.main" sx={{ pl: "33.5px" }}>
                  Associado, mas o e-mail não foi enviado — reenvie por "Enviar
                  acesso por e-mail".
                </Typography>
              )}
              {!entry.error && entry.emailDelivered === null && (
                <Typography variant="caption" color="info.main" sx={{ pl: "33.5px" }}>
                  Associado. Nenhum e-mail era devido para este respondente.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
        {(hasErrors || hasUndelivered) && submissionPhase === "done" && (
          // Sem erro nenhum, só e-mail não entregue, a alocação está de pé: o
          // fechamento manual é o mesmo caminho do automático, e a listagem
          // precisa recarregar. Com erro, mantém-se o fechamento simples.
          <Button
            variant={hasErrors ? "outlined" : "contained"}
            onClick={() =>
              hasErrors
                ? this.props.delegate.shouldCloseModal()
                : this.props.delegate.didAllocateRespondents()
            }
            sx={{ alignSelf: "flex-end", mt: 1 }}
          >
            Fechar
          </Button>
        )}
      </Box>
    );
  }

  render() {
    const { submissionPhase, usersToAssign } = this.state;
    const { assignment } = this.props;
    const isIdle = submissionPhase === "idle";
    const isSubmitting = submissionPhase === "inviting" || submissionPhase === "done";

    return (
      <Dialog
        open
        onClose={() => isIdle && this.props.delegate.shouldCloseModal()}
        fullWidth
        maxWidth="sm"
        variant="modal"
      >
        <DialogTitle variant="modal">
          <GroupAddIcon />
          Alocar respondentes
        </DialogTitle>
        <DialogContent variant="modal">
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              Aplicação: <strong>{assignment.title}</strong>
            </Typography>
            {isSubmitting ? this.renderInviteLog() : this.renderUserPanel()}
          </Box>
        </DialogContent>
        {isIdle && (
          <DialogActions variant="modal" sx={{ gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => this.props.delegate.shouldCloseModal()}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={usersToAssign.length === 0}
              onClick={() => this.handleAllocate()}
            >
              Alocar respondentes
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default AllocateRespondentsModal;
